<template>
  <div class="advise-list">
    <div class="advise-card" v-for="(item, i) in items" :key="i">
      <div class="advise-header">
        <span class="advise-num">{{ i + 1 }}</span>
        <span class="advise-source">{{ item.sourceLabel }}</span>
        <span :class="['advise-badge', item.calculated ? 'badge-ok' : 'badge-warn']">{{ item.calculated ? 'Рассчитан' : 'Не рассчитан' }}</span>
      </div>
      <div class="advise-prices">
        <div class="price-cell">
          <span class="price-label">Входная</span>
          <span class="price-value">{{ item.inputPrice }}</span>
        </div>
        <span class="price-arrow">&rarr;</span>
        <div class="price-cell">
          <span class="price-label">Выходная</span>
          <span class="price-value">{{ item.outputPrice }}</span>
        </div>
      </div>
      <div v-if="item.inputKeys.length" class="advise-inputs">
        <span class="input-label">Входные данные:</span>
        <div class="input-grid">
          <div class="input-item copy-row" v-for="ik in item.inputKeys" :key="ik.key" @click="copyValue(ik.value)" :title="copyTitle(ik.value)">
            <span class="input-key">{{ ik.key }}</span>
            <span class="input-val">{{ ik.value }}</span>
          </div>
        </div>
      </div>
      <div v-if="item.rules.length" class="advise-rules">
        <span class="input-label">Правила ({{ item.rules.length }}):</span>
        <div class="rule-list">
          <div class="rule-card" v-for="(rule, ri) in item.rules" :key="ri">
            <div class="rule-top">
              <span :class="['rule-action', `action-${rule.actionClass}`]">{{ rule.actionLabel }}</span>
              <span class="rule-name">{{ rule.ruleName }}</span>
              <span class="rule-group" v-if="rule.group">Гр. {{ rule.group }}</span>
            </div>
            <div class="rule-detail" v-if="rule.baseValue">
              <span class="rule-dt-label">Базовая:</span>
              <span class="rule-dt-value copy-row" @click="copyValue(rule.baseValue)" :title="copyTitle(rule.baseValue)">{{ rule.baseValue }}</span>
            </div>
            <div class="rule-detail" v-if="rule.adjType || rule.adjValue">
              <span class="rule-dt-label">Корректировка:</span>
              <span class="rule-dt-value copy-row" @click="copyValue(rule.adjLabel)" :title="copyTitle(rule.adjLabel)">{{ rule.adjLabel }}</span>
            </div>
            <div class="rule-msg" v-if="rule.message">{{ rule.message }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EMPTY_VALUE } from '@/pages/pureBidding/helpers';
import { createToaster } from '@meforma/vue-toaster';

const toaster = createToaster();

export default {
  name: 'AdviseRules',
  props: {
    advise: { type: Array, default: () => [] },
  },
  computed: {
    items() {
      if (!Array.isArray(this.advise)) return [];
      return this.advise.map((a) => {
        const logResult = a.log_result || {};
        const logInput = logResult.log_input || {};
        const logOutput = logResult.log_output || [];
        return {
          sourceLabel: this.sourceLabel(a.source),
          calculated: !!a.is_calculated,
          inputPrice: this.money(a.input_price),
          outputPrice: this.money(a.output_price),
          inputKeys: Object.entries(logInput).map(([key, val]) => ({
            key: this.inputKeyLabel(key),
            value: this.inputValFormat(val),
          })),
          rules: logOutput.map((r) => ({
            actionLabel: this.actionLabel(r.action),
            actionClass: r.action || '',
            ruleName: r.rule_name || '—',
            group: r.group,
            baseValue: r.base_value != null ? this.money(r.base_value) : null,
            adjType: r.adj_type ? this.adjTypeLabel(r.adj_type) : null,
            adjValue: r.adj_value != null ? this.money(r.adj_value) : null,
            adjLabel: [r.adj_type ? this.adjTypeLabel(r.adj_type) : '', r.adj_value != null ? this.money(r.adj_value) : ''].filter(Boolean).join(': '),
            message: r.message || null,
          })),
        };
      });
    },
  },
  methods: {
    money(val) {
      if (typeof val !== 'number') return EMPTY_VALUE;
      return val.toLocaleString('ru-RU');
    },
    copyTitle(value) {
      return value && value !== EMPTY_VALUE ? 'Скопировать' : '';
    },
    copyValue(value) {
      if (!value || value === EMPTY_VALUE) return;
      navigator.clipboard?.writeText(String(value)).then(() => {
        toaster.success('Скопировано');
      }).catch(() => {});
    },
    sourceLabel(source) {
      const map = { avg: 'По ставкам ATI', mem: 'По памяти', order: 'По заявке', undefined: 'Не указано' };
      return map[source] || source || '—';
    },
    actionLabel(action) {
      const map = { apply: 'Применено', exclude: 'Исключено', error: 'Ошибка' };
      return map[action] || action || '—';
    },
    adjTypeLabel(type) {
      const map = { percent: '%', fixed: 'Фикс.', multiplier: 'Множитель', override: 'Переопределение', discount: 'Скидка', markup: 'Наценка' };
      return map[type] || type || '';
    },
    inputKeyLabel(key) {
      const map = {
        base_price: 'Базовая цена', distance: 'Расстояние', city_from: 'Город отправки',
        city_to: 'Город прибытия', vehicle_type: 'Тип ТС', tonnage: 'Тоннаж',
        weight: 'Вес', volume: 'Объём', pallet: 'Паллеты', cargo_type: 'Тип груза',
        hazardous: 'Опасный', client_price: 'Цена клиента', carrier_price: 'Цена перевозчика',
        route_count: 'Кол-во маршрутов', trip_factor: 'Фактор', loading_date: 'Дата погрузки',
        platform: 'Платформа', service_name: 'Сервис',
      };
      return map[key] || key;
    },
    inputValFormat(val) {
      if (val === null || val === undefined) return EMPTY_VALUE;
      if (typeof val === 'boolean') return val ? 'Да' : 'Нет';
      if (typeof val === 'number') return val.toLocaleString('ru-RU');
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    },
  },
};
</script>

<style scoped>
.advise-list { display: flex; flex-direction: column; gap: 12px; }

.advise-card {
  padding: 12px;
  background: var(--details-card-bg, #e9edf3);
  border-radius: 8px;
}

.advise-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.advise-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  background: #6366f1;
}

.advise-source {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.advise-badge {
  margin-left: auto;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 500;
}

.badge-ok { background: #dcfce7; color: #15803d; }
.badge-warn { background: #fef9c3; color: #a16207; }

.advise-prices {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  padding: 6px 0;
}

.price-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.price-label { font-size: 11px; color: #7b8087; }
.price-value { font-size: 16px; font-weight: 700; color: #333; }
.price-arrow { font-size: 18px; color: #9ca3af; }

.advise-inputs {
  margin-bottom: 8px;
}

.input-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.input-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.input-item {
  display: flex;
  gap: 4px;
  padding: 3px 8px;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #e5e7eb;
}

.input-key { color: #7b8087; }
.input-val { font-weight: 500; color: #333; }

.advise-rules {
  margin-top: 4px;
}

.rule-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.rule-card {
  padding: 8px 10px;
  background: #fff;
  border-radius: 6px;
  border-left: 3px solid #d1d5db;
}

.rule-card:has(.action-apply) { border-left-color: #22c55e; }
.rule-card:has(.action-exclude) { border-left-color: #ef4444; }
.rule-card:has(.action-error) { border-left-color: #f59e0b; }

.rule-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.rule-action {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 3px;
}

.action-apply { background: #dcfce7; color: #15803d; }
.action-exclude { background: #fee2e2; color: #b91c1c; }
.action-error { background: #fef3c7; color: #b45309; }

.rule-name {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.rule-group {
  margin-left: auto;
  font-size: 11px;
  color: #9ca3af;
}

.rule-detail {
  display: flex;
  gap: 6px;
  font-size: 12px;
  margin-top: 2px;
}

.rule-dt-label { color: #7b8087; min-width: 110px; }
.rule-dt-value { font-weight: 500; color: #333; }

.rule-msg {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}

.copy-row {
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s;
}

.copy-row:hover { background: #eef4ff; }

.copy-row:hover .rule-dt-value,
.copy-row:hover .input-val { color: #1d4ed8; }
</style>
